import { setI18n } from '@magicdoor/ui';
import { Routes, Route } from '@solidjs/router';
import { createEffect, ErrorBoundary, Show, lazy } from 'solid-js';
import NotFound from '~/components/common/NotFound';
import { FullScreenLayout, Layout } from '~/components/layouts';
import { Dialogs, Toaster } from '~/components/ui';
import { useCompanies } from '~/contexts/global';
import Navigator from '~/framework/Navigator';
import en from '~/locales/en/translation.json';
import zh from '~/locales/zh/translation.json';
import DashboardPage from '~/pages/dashboard/Dashboard';
import ErrorFallback from '~/pages/error/ErrorFallback';
import { IconsPage } from '~/pages/test/IconsPage';
import { PlaygroundPage } from '~/pages/test/PlaygroundPage';
import { ShowErrorFallback } from '~/pages/test/ShowErrorFallback';
import UnauthorizedPage from '~/pages/test/UnauthorizedPage';
import UnknownErrorPage from '~/pages/test/UnknownErrorPage';
import { UploadPage } from '~/pages/test/UploadPage';
import { ErrorHandler } from '~/pdfsigner/ui/context/ErrorContext';
import { configureDayjsTimezone } from '~/utils/date';
import type { Component } from 'solid-js';

const AiRunbooksRoutes = lazy(() => import('~/pages/ai-runbooks').then((m) => ({ default: m.AiRunbooksRoutes })));
const ViolationsRoutes = lazy(() => import('~/pages/violations').then((m) => ({ default: m.ViolationsRoutes })));
const AddOfflinePaymentRoutes = lazy(() => import('~/pages/add-offline-payment').then((m) => ({ default: m.AddOfflinePaymentRoutes })));
const AnnouncementRoutes = lazy(() => import('~/pages/announcements').then((m) => ({ default: m.default })));
const ArchiveMessagesRoutes = lazy(() => import('~/pages/archive-messages').then((m) => ({ default: m.default })));
const BankAccountsRoutes = lazy(() => import('~/pages/bank-accounts').then((m) => ({ default: m.default })));
const BillsRoutes = lazy(() => import('~/pages/bills').then((m) => ({ default: m.default })));
const ChartOfAccountRoutes = lazy(() => import('~/pages/chart-of-accounts').then((m) => ({ default: m.default })));
const ChatRoutes = lazy(() => import('~/pages/chats').then((m) => ({ default: m.default })));
const ChecksRoutes = lazy(() => import('~/pages/checks').then((m) => ({ default: m.ChecksRoutes })));
const CompanyRevenueRoutes = lazy(() => import('~/pages/company-revenue').then((m) => ({ default: m.CompanyRevenueRoutes })));
const DepositSlipsRoutes = lazy(() => import('~/pages/deposit-slips').then((m) => ({ default: m.DepositSlipsRoutes })));
const LeaseTemplateRoutes = lazy(() => import('~/pages/lease-templates').then((m) => ({ default: m.LeaseTemplateRoutes })));
const LeasesRoutes = lazy(() => import('~/pages/leases').then((m) => ({ default: m.LeasesRoutes })));
const ListingRoutes = lazy(() => import('~/pages/listing').then((m) => ({ default: m.default })));
const OnboardingRoutes = lazy(() => import('~/pages/onboarding').then((m) => ({ default: m.OnboardingRoutes })));
const MaintenanceRequestRoutes = lazy(() => import('~/pages/maintenance-request').then((m) => ({ default: m.default })));
const OwnerRoutes = lazy(() => import('~/pages/owner').then((m) => ({ default: m.OwnerRoutes })));
const OwnerFundsRoutes = lazy(() => import('~/pages/owner-funds').then((m) => ({ default: m.OwnerFundsRoutes })));
const PortfoliosRoutes = lazy(() => import('~/pages/portfolios').then((m) => ({ default: m.PortfoliosRoutes })));
const PropertyManagerRoutes = lazy(() => import('~/pages/property-managers').then((m) => ({ default: m.PropertyManagerRoutes })));
const RentalApplicationRoutes = lazy(() => import('~/pages/rental-applications').then((m) => ({ default: m.RentalApplicationRoutes })));
const ReportsRoutes = lazy(() => import('~/pages/reports').then((m) => ({ default: m.default })));
const SettingRoutes = lazy(() => import('~/pages/settings').then((m) => ({ default: m.default })));
const TenantsRoutes = lazy(() => import('~/pages/tenants').then((m) => ({ default: m.default })));
const UnreadMessagesRoutes = lazy(() => import('~/pages/unread-messages').then((m) => ({ default: m.default })));
const VendorRoutes = lazy(() => import('~/pages/vendors').then((m) => ({ default: m.default })));
const WorkOrderRoutes = lazy(() => import('~/pages/work-orders').then((m) => ({ default: m.WorkOrderRoutes })));

const TailwindIndicator = () => (
  <Show when={import.meta.env.DEV}>
    <div class="fixed bottom-1 left-1 z-50 flex size-10 items-center justify-center rounded-full border bg-white font-mono text-sm opacity-50 shadow hover-allowed:hover:opacity-100">
      <div class="block sm:hidden">xs</div>
      <div class="hidden sm:block md:hidden">sm</div>
      <div class="hidden md:block lg:hidden">md</div>
      <div class="hidden lg:block xl:hidden">lg</div>
      <div class="hidden xl:block 2xl:hidden">xl</div>
      <div class="hidden 2xl:block">2xl</div>
    </div>
  </Show>
);

setI18n({
  locals: {
    en,
    zh,
  },
});

const App: Component = () => {
  const { companies } = useCompanies();
  createEffect(() => {
    if (companies()) {
      configureDayjsTimezone();
    }
  });

  return (
    <ErrorBoundary
      fallback={(err, reset) => {
        return <ErrorFallback error={err} reset={reset} />;
      }}>
      <ErrorHandler>
        <Routes>
          <Navigator />
          <Route path="/" component={Layout}>
            <Route path="/" component={DashboardPage} />
            <Route path="/onboarding/*" component={OnboardingRoutes} />
            <Route path="/listings/*" component={ListingRoutes} />
            <Route path="/accounting">
              <Route path="/bank-accounts/*" component={BankAccountsRoutes} />
              <Route path="/bills/*" component={BillsRoutes} />
              <Route path="/chart-of-accounts/*" component={ChartOfAccountRoutes} />
              <Route path="/company-revenue/*" component={CompanyRevenueRoutes} />
              <Route path="/owner-funds/*" component={OwnerFundsRoutes} />
              <Route path="/deposit-slips/*" component={DepositSlipsRoutes} />
              <Route path="/checks/*" component={ChecksRoutes} />
            </Route>
            <Route path="/reports/*" component={ReportsRoutes} />
            <Route path="/users">
              <Route path="/tenants/*" component={TenantsRoutes} />
              <Route path="/property-managers/*" component={PropertyManagerRoutes} />
              <Route path="/owners/*" component={OwnerRoutes} />
            </Route>
            <Route path="/maintenance">
              <Route path="/vendors/*" component={VendorRoutes} />
              <Route path="/maintenance-request/*" component={MaintenanceRequestRoutes} />
              <Route path="/work-orders/*" component={WorkOrderRoutes} />
              <Route path="/ai-runbooks/*" component={AiRunbooksRoutes} />
            </Route>
            <Route path="/communications">
              <Route path="/announcements/*" component={AnnouncementRoutes} />
              <Route path="/chats/*" component={ChatRoutes} />
              <Route path="/dashboard/*" component={UnreadMessagesRoutes} />
              <Route path="/archive-messages/*" component={ArchiveMessagesRoutes} />
            </Route>
            <Route path="/associations">
              <Route path="/violations/*" component={ViolationsRoutes} />
            </Route>
            <Route path="/portfolios/*" component={PortfoliosRoutes} />
            <Route path="/leasing">
              <Route path="/leases/*" component={LeasesRoutes} />
              <Route path="/lease-templates/*" component={LeaseTemplateRoutes} />
              <Route path="/add-offline-payment/*" component={AddOfflinePaymentRoutes} />
              <Route path="/*" component={RentalApplicationRoutes} />
            </Route>
            <Route path="/settings/*" component={SettingRoutes} />
          </Route>

          <Route path="/test">
            <Route path="unauthorized" component={UnauthorizedPage} />
            <Route path="unknown" component={UnknownErrorPage} />
            <Route path="play" component={PlaygroundPage} />
            <Route path="icons" component={IconsPage} />
            <Route path="error" component={ShowErrorFallback} />
            <Route path="upload" component={UploadPage} />
          </Route>
          <Route path="*" component={FullScreenLayout}>
            <Route path="*" component={NotFound} />
          </Route>
        </Routes>
        <Dialogs />
        <Toaster />
        <TailwindIndicator />
      </ErrorHandler>
    </ErrorBoundary>
  );
};

export default App;
